import React, { memo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import DataObject from '@mui/icons-material/DataObject';
import Upload from '@mui/icons-material/Upload';
import type { SwaggerFile } from '../../types/swaggerFile';
import { monoSx } from '../dashboard/styles';
import { formatFileSize, formatUploaded } from '../swagger/swaggerFileModel';

interface SwaggerFileListProps {
  files: SwaggerFile[];
  loading: boolean;
  loadError: string | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const SwaggerFileList: React.FC<SwaggerFileListProps> = ({ files, loading, loadError, selectedId, onSelect }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    {loading ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, color: 'text.secondary', fontSize: 13 }}>
        <CircularProgress size={18} />
        Loading swagger files…
      </Box>
    ) : loadError ? (
      <Alert severity="error">{loadError}</Alert>
    ) : files.length === 0 ? (
      <Typography sx={{ fontSize: 13, color: 'text.secondary', p: 1.5, border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 3 }}>
        No swagger files uploaded yet.
      </Typography>
    ) : (
      <Box role="radiogroup" aria-label="Swagger file" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {files.map((file) => {
          const selected = file.id === selectedId;
          return (
            <ButtonBase
              key={file.id}
              role="radio"
              aria-checked={selected}
              onClick={() => onSelect(file.id)}
              sx={(theme) => ({
                justifyContent: 'flex-start',
                textAlign: 'left',
                gap: 1.5,
                borderRadius: 3,
                // A 2px selected border would otherwise shift the content by 1px.
                border: `${selected ? 2 : 1}px solid`,
                py: selected ? '11px' : '12px',
                pl: selected ? '11px' : '12px',
                pr: selected ? '13px' : '14px',
                borderColor: selected ? theme.palette.primary.dark : theme.palette.divider,
                bgcolor: selected ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                transition: 'border-color 120ms, background-color 120ms',
                '&:hover': { borderColor: theme.palette.primary.dark },
                '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
              })}
            >
              <Radio
                checked={selected}
                tabIndex={-1}
                size="small"
                disableRipple
                inputProps={{ 'aria-hidden': true }}
                sx={{ p: 0, color: 'text.secondary', '&.Mui-checked': { color: 'primary.dark' } }}
              />
              <Box
                aria-hidden
                sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'action.hover', color: 'text.secondary' }}
              >
                <DataObject sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }} title={file.originalName}>
                  {file.description || file.originalName}
                </Typography>
                <Typography noWrap sx={{ ...monoSx, fontSize: 11, color: 'text.secondary' }}>
                  {file.originalName}
                </Typography>
              </Box>
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'flex-end', gap: 0.25, fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                <span>{formatFileSize(file.size)}</span>
                <span>{formatUploaded(file.uploadDate).day}</span>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    )}
    <Button
      component={RouterLink}
      to="/swagger-files"
      startIcon={<Upload />}
      sx={{
        alignSelf: 'flex-start',
        height: 36,
        pl: 1.25,
        pr: 1.75,
        borderRadius: 4.5,
        border: 1,
        borderStyle: 'dashed',
        borderColor: 'divider',
        color: 'primary.dark',
        fontSize: 13,
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': { bgcolor: 'brand.container', borderColor: 'transparent' },
      }}
    >
      Upload new Swagger file
    </Button>
  </Box>
);

export default memo(SwaggerFileList);
